import { Grid } from '@mui/material';
import { validLanguages } from '@nx-smartmonkey/shared/domain';
import { HSpacer, RoutalDialog, RoutalSelect, RoutalSelectMultiSelectOption, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { HeaderContent } from '../../../components/HeaderContent';
import * as routing from '../../../constants/Routing';
import { useCreateTranslation } from '../../../hooks/backoffice/translations/useCreateTranslation';
import { useSearchTranslationsPredicates } from '../../../hooks/backoffice/translations/useSearchTranslationsPredicates';
import Button from '../../../react-components/Button';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';
import { getFlagByLocale } from '../shared';

const AdministrationTranslationsHeaderContainer = styled.div`
  display: relative;
  background-color: transparent;
  padding: 16px 22px;
  max-height: 117px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e0e0e0;
`;

type AdministrationTranslationsHeaderProps = {
  onCreateTranslation: () => void;
};

export const AdministrationTranslationsHeader = ({ onCreateTranslation }: AdministrationTranslationsHeaderProps) => {
  const navigate = useNavigate();
  const { language, predicatesBase64 } = useSearchTranslationsPredicates();
  const [newKey, setNewKey] = useState<string>(``);
  const [translation, setTranslation] = useState<string>(``);
  const [createTranslationDialog, setCreateTranslationDialog] = useState<boolean>(false);
  const [domain, setDomain] = useState<`planner` | `customers` | `drivers` | `supervisors`>(`planner`);
  const { run: createTranslation, success, error, isLoading } = useCreateTranslation();

  useEffect(() => {
    if (success) {
      setNewKey(``);
      setTranslation(``);
      setCreateTranslationDialog(false);
      onCreateTranslation();
    }
  }, [success, error, onCreateTranslation]);

  return (
    <>
      <AdministrationTranslationsHeaderContainer>
        <HeaderContent
          titleIntlId={`administration.translations`}
          styles={{
            container: {
              backgroundColor: `transparent`,
            },
          }}
        />

        <Grid container direction="row" justifyContent="flex-end" alignItems="center">
          <RoutalSelect
            id="filter-language"
            containerStyle={{
              width: `230px`,
            }}
            popoverProps={{
              options: validLanguages.map(
                (validLanguage): RoutalSelectMultiSelectOption => ({
                  key: validLanguage,
                  onClick: () => {
                    navigate(
                      `${routing.BACKOFFICE.TRANSLATIONS}?predicates=${predicatesBase64}&language=${validLanguage}`
                    );
                  },
                  content: (
                    <Grid
                      key={`${validLanguage}-column`}
                      item
                      container
                      direction="row"
                      wrap="nowrap"
                      justifyContent="flex-start"
                      alignItems="center"
                    >
                      <img src={getFlagByLocale(validLanguage)} alt="" width="20px" draggable="false" />
                      <HSpacer small />
                      <Text
                        style={{ cursor: `pointer` }}
                        isBold
                        intlId={`administration.translations.language.${validLanguage}`}
                      />
                    </Grid>
                  ),
                })
              ),
            }}
          >
            <Grid item container direction="row" wrap="nowrap" justifyContent="flex-start" alignItems="center">
              <img src={getFlagByLocale(language)} alt="" width="20px" draggable="false" />
              <HSpacer small />
              <Text style={{ cursor: `pointer` }} isBold intlId={`administration.translations.language.${language}`} />
            </Grid>
          </RoutalSelect>

          <HSpacer medium />

          <Button
            id="translations-new-entry"
            key="translations-new-entry"
            style={{
              width: `150px`,
            }}
            onClick={(e: any) => {
              setCreateTranslationDialog(true);
            }}
            intlId="administration.translations.add_entry"
          />
        </Grid>
      </AdministrationTranslationsHeaderContainer>

      <RoutalDialog
        id="create-translation-dialog"
        open={createTranslationDialog}
        dialogProps={{
          disableBackdropClick: true,
          disableEscapeKeyDown: true,
        }}
        onCloseDialog={() => {
          setNewKey(``);
          setTranslation(``);
          setCreateTranslationDialog(false);
        }}
        titleContent={<Text variant="h6" intlId="administration.translations.new_translation_dialog.title" />}
        titleRightContent={
          <Button
            style={{
              width: `80px`,
            }}
            onClick={() => {
              if (!isLoading && newKey !== ``) {
                createTranslation({ language, key: newKey, value: translation, domain: domain });
              }
            }}
            intlId="administration.translations.new_translation_dialog.button"
          />
        }
        showFooter={false}
      >
        <Grid container direction="column" style={{ marginBottom: `16px` }}>
          <Grid item>
            <Text intlId="administration.translations.domain" variant="label" style={{ marginBottom: `8px` }} />
            <RoutalSelect
              id="domain-filter"
              popoverProps={{
                options: [`planner`, `drivers`, `customers`, `supervisors`].map(
                  (d, index): RoutalSelectMultiSelectOption => ({
                    key: d,
                    listItemStyles: {
                      color: `#929599`,
                      fontSize: `12px`,
                      paddingLeft: `9px`,
                      paddingRight: `8px`,
                      overflow: `hidden`,
                    },
                    onClick: () => {
                      setDomain(d as any);
                    },
                    content: (
                      <Grid container direction="row" wrap="nowrap" alignItems="center" style={{ overflow: `hidden` }}>
                        <Text
                          tagName="span"
                          style={{
                            textAlign: `justify`,
                            whiteSpace: `nowrap`,
                            overflow: `hidden`,
                            width: `100%`,
                            textOverflow: `ellipsis`,
                            cursor: `pointer`,
                          }}
                          intlId={`administration.translations.domain.${d}`}
                        />
                      </Grid>
                    ),
                  })
                ),
              }}
            >
              <Text
                style={{
                  cursor: `pointer`,
                  width: `100%`,
                  textAlign: `justify`,
                }}
                intlId={`administration.translations.domain.${domain}`}
              />
            </RoutalSelect>
          </Grid>

          <VSpacer medium />

          <Grid item>
            <Text intlId="administration.translations.identifier" variant="label" style={{ marginBottom: `8px` }} />
            <InputText
              autoFocus
              inputContainerStyles={{ height: `100%`, width: `100%` }}
              fieldValue={newKey}
              placeholderIntlId="administration.translations.identifier"
              onChange={(val?: string) => {
                setNewKey(val ?? ``);
              }}
            />
          </Grid>

          <VSpacer medium />

          <Grid item>
            <Text
              intlId="administration.translations.new_translation_dialog.spanish_translation"
              variant="label"
              style={{ marginBottom: `8px` }}
            />
            <InputText
              inputContainerStyles={{ height: `100%`, width: `100%` }}
              fieldValue={translation}
              placeholderIntlId="administration.translations.translation"
              onChange={(val?: string) => {
                setTranslation(val ?? ``);
              }}
            />
          </Grid>
        </Grid>
      </RoutalDialog>
    </>
  );
};
